import { SuccessfulAddRoleDialog } from '../components/roles/successfullAddRoleDialog';
import { AddRoleDialog } from '../components/roles/addRoleDialog';
import { useState, useCallback, useEffect } from 'react';
import RolePageHeader from '../components/roles/rolePageHeader';
import RoleFiltration from '../components/roles/roleFiltration';
import { CustomErrorLogger } from '../utils/CustomErrorLogger';
import { Divider, Box, LinearProgress } from '@mui/material';
import { useHasPermission } from '../hooks/useHasPermission';
import { isObjectEmpty } from '../utils/isObjectEmpty';
import RolesList from '../components/roles/rolesList';
import {
    ListNavigationProvider,
    useListPageNavigation,
    usePersistedListParams,
} from '../hooks/listNavigation';
import NoRecord from '../components/NoRecord';
import Page from '../components/Page';
import '../css/roles/roles.css';
import axios from 'axios';

const Roles = () => {

    const hasRoleCreatePermission = useHasPermission('role_create');

    const [roleId, setRoleId] = useState(null);
    const [openAddRole, setOpenAddRole] = useState(false);
    const [openSuccessfulAddRole, setOpenSuccessfulAddRole] = useState(false);

    const [loading, setLoading] = useState(true);
    const [roles, setRoles] = useState([])
    const [totalRoles, setTotalRoles] = useState('');
    const [filteredRolesCount, setFilteredRolesCount] = useState('');
    const [rolesFetched, setRolesFetched] = useState(0);
    const defaultSortBy = {
        name: null,
        description: null,
    };
    const {
        filters: roleFilters,
        setFilters: setRoleFilters,
        sortBy,
        setSortBy,
    } = usePersistedListParams({
        keys: {
            filters: 'roleFilters',
            sortBy: 'roleSortBy',
        },
        defaults: {
            filters: { debouncedSearch: '' },
            sortBy: defaultSortBy,
        },
    });
    const { scrollRef, offset, setOffset, navigationValue } = useListPageNavigation({
        id: 'roles',
        pathname: '/dashboard/roles',
        displayMode: 'list',
        itemSizes: { list: 70 },
        resetOnFilters: roleFilters,
        resetOnSort: sortBy,
        ready: !loading,
    });

    const limit = 30;

    const filtersEmpty = isObjectEmpty(roleFilters);

    useEffect(() => {
        setLoading(true)
    }, [roleFilters]);


    const fetchRoles = useCallback(async () => {
        try {
            const adjustedOffset = Math.max(offset - 10, 0) || 0; // Ensure offset is not negative
            const response = await axios.get('role/list', { params: { offset: adjustedOffset, limit, filters: roleFilters, sortBy } });
            if (response.status === 200 || response.status === 201) {
                // Create a copy of the current files array
                const updatedRoles = [];
                // Update the fetched records in their correct positions
                response?.data?.roles?.forEach((role, index) => { updatedRoles[adjustedOffset + index] = role });
                // Update the state with the new roles array
                setRoles(updatedRoles);
                setFilteredRolesCount(response?.data?.filteredRolesCount);
                setTotalRoles(response?.data?.totalRoles);
                setLoading(false);
            }
        } catch (error) {
            setLoading(false);
            console.error('Error Fetching roles:', error);
            CustomErrorLogger(error, 'fetch roles in roles page');
        }
    }, [offset, roleFilters, sortBy]);

    useEffect(() => {
        fetchRoles()
    }, [fetchRoles]);

    return (
        <ListNavigationProvider value={navigationValue}>
        <Page title="Rôles" list={true}>
            <RolePageHeader setOpenAddRole={setOpenAddRole} />
            <Divider />
            {loading ?
                <Box>
                    <RoleFiltration
                        filtersEmpty={filtersEmpty}
                        roleFilters={roleFilters}
                        setRoleFilters={setRoleFilters}
                        totalRoles={totalRoles}
                        rolesFetched={rolesFetched}
                        filteredRolesCount={filteredRolesCount}
                    />
                    <LinearProgress sx={{ '& .MuiLinearProgress-bar': { backgroundColor: '#EA5E00' }, backgroundColor: '#A8ADB8' }} />
                </Box> :
                totalRoles === 0 ?
                    <NoRecord
                        description="Aucun machine ajouté pour le moment"
                        buttonTitle="Ajouter une machine"
                        handleOpenDialog={() => setOpenAddRole(true)}
                        hasPermission={hasRoleCreatePermission}
                    /> :
                    <Box>
                        <RoleFiltration
                            filtersEmpty={filtersEmpty}
                            roleFilters={roleFilters}
                            setRoleFilters={setRoleFilters}
                            totalRoles={totalRoles}
                            rolesFetched={rolesFetched}
                            filteredRolesCount={filteredRolesCount}
                        />

                        <RolesList
                            scrollRef={scrollRef}
                            roles={roles}
                            sortBy={sortBy}
                            setSortBy={setSortBy}
                            setOffset={setOffset}
                            filteredRolesCount={filteredRolesCount}
                            setRolesFetched={setRolesFetched}
                        />

                    </Box>
            }

            {openAddRole &&
                <AddRoleDialog
                    roles={roles}
                    setRoles={setRoles}
                    totalRoles={totalRoles}
                    setTotalRoles={setTotalRoles}
                    filteredRolesCount={filteredRolesCount}
                    setFilteredRolesCount={setFilteredRolesCount}
                    openAddRole={openAddRole}
                    setOpenAddRole={setOpenAddRole}
                    setOpenSuccessfulAddRole={setOpenSuccessfulAddRole}
                    setRoleId={setRoleId}
                />
            }

            {openSuccessfulAddRole &&
                <SuccessfulAddRoleDialog
                    openSuccessfulAddRole={openSuccessfulAddRole}
                    setOpenSuccessfulAddRole={setOpenSuccessfulAddRole}
                    roleId={roleId}
                    setOpenAddRole={setOpenAddRole}
                />
            }
        </Page>
        </ListNavigationProvider>
    );
};

export default Roles;